
import { useState } from 'react';
import type React from 'react';

export default function Contact() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);

    try {
      const res = await fetch('https://readdy.ai/api/form/d6hesgsbcbcsu13rg6ag', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setSubmitStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative py-32 px-8 overflow-hidden bg-black">
      {/* Background image with dark overlay */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/251880e60904db8c9227d729ac15be49.jpg"
          alt="聯絡背景"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-black/90" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-transparent to-black/70" />
      </div>

      {/* Top divider line */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[1px] bg-gradient-to-r from-transparent via-amber-500/40 to-transparent" />

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Section label */}
        <div className="flex items-center gap-4 mb-12">
          <div className="w-12 h-px bg-gradient-to-r from-amber-400 to-transparent" />
          <span
            className="text-amber-400/80 text-xs tracking-[0.4em] font-light uppercase"
            style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
          >
            CONTACT · COLLABORATE
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left info */}
          <div>
            <h2
              className="font-black leading-none mb-8"
              style={{ fontFamily: "'Noto Serif TC', serif" }}
            >
              <div
                className="text-5xl md:text-7xl"
                style={{
                  background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 30%, #fde68a 60%, #d97706 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  filter: 'drop-shadow(0 0 40px rgba(251,191,36,0.4))',
                }}
              >
                讓我們一起
              </div>
              <div className="text-5xl md:text-7xl text-white drop-shadow-[0_0_20px_rgba(255,255,255,0.15)]">
                創造魔幻
              </div>
            </h2>

            <p
              className="text-white/80 text-lg leading-relaxed mb-12"
              style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
            >
              專業 AI 影視製作服務，打造獨一無二的視覺體驗。<br />
              無論是品牌形象、商業廣告或虛擬 IP，歡迎與我聯繫。
            </p>

            <div className="space-y-6">
              {[
                { icon: 'ri-mail-line', label: '電子郵件', value: 'adam.chen0706@gmail.com' },
                { icon: 'ri-phone-line', label: '聯絡電話', value: '+886 987-080-355' },
                { icon: 'ri-map-pin-line', label: '工作室地址', value: '台北市文山區木柵路一段83號' },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-5">
                  <div
                    className="w-12 h-12 flex items-center justify-center rounded-sm border border-amber-500/40 bg-amber-500/10"
                  >
                    <i className={`${item.icon} text-xl text-amber-400`}></i>
                  </div>
                  <div>
                    <p
                      className="text-xs text-amber-400/80 tracking-widest mb-0.5"
                      style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
                    >
                      {item.label}
                    </p>
                    <p
                      className="text-white font-light"
                      style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
                    >
                      {item.value}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right form */}
          <div className="border border-amber-500/20 bg-black/60 backdrop-blur-lg p-8 rounded-sm shadow-2xl shadow-amber-500/10">
            <h3
              className="text-2xl font-bold text-white mb-2"
              style={{ fontFamily: "'Noto Serif TC', serif" }}
            >
              開始合作
            </h3>
            <div className="w-10 h-px bg-gradient-to-r from-amber-400 to-transparent mb-8" />

            <form onSubmit={handleSubmit} data-readdy-form className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-white/80 text-xs tracking-widest mb-2 uppercase"
                    style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
                  >
                    姓名 *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-3 bg-white/5 border border-amber-500/20 rounded-sm text-white placeholder-white/30 focus:outline-none focus:border-amber-400/60 focus:ring-1 focus:ring-amber-400/30 transition-all duration-300 text-sm"
                    style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
                    placeholder="您的姓名"
                  />
                </div>
                <div>
                  <label
                    htmlFor="company"
                    className="block text-white/80 text-xs tracking-widest mb-2 uppercase"
                    style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
                  >
                    公司名稱
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    className="w-full px-4 py-3 bg-white/5 border border-amber-500/20 rounded-sm text-white placeholder-white/30 focus:outline-none focus:border-amber-400/60 focus:ring-1 focus:ring-amber-400/30 transition-all duration-300 text-sm"
                    style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
                    placeholder="公司名稱"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="block text-white/80 text-xs tracking-widest mb-2 uppercase"
                  style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
                >
                  電子郵件 *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  className="w-full px-4 py-3 bg-white/5 border border-amber-500/20 rounded-sm text-white placeholder-white/30 focus:outline-none focus:border-amber-400/60 focus:ring-1 focus:ring-amber-400/30 transition-all duration-300 text-sm"
                  style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="block text-white/80 text-xs tracking-widest mb-2 uppercase"
                  style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
                >
                  專案需求 *
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={4}
                  maxLength={500}
                  onChange={(e) => setCharCount(e.target.value.length)}
                  className="w-full px-4 py-3 bg-white/5 border border-amber-500/20 rounded-sm text-white placeholder-white/30 focus:outline-none focus:border-amber-400/60 focus:ring-1 focus:ring-amber-400/30 transition-all duration-300 resize-none text-sm"
                  style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
                  placeholder="請描述您的專案需求..."
                />
                <p className="text-white/50 text-xs mt-1 text-right">{charCount} / 500</p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-gradient-to-r from-amber-500 to-yellow-400 text-black font-bold text-sm tracking-widest rounded-sm hover:shadow-2xl hover:shadow-amber-500/50 transition-all duration-300 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer"
                style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line text-lg animate-spin"></i>
                    <span>傳送中...</span>
                  </>
                ) : (
                  <>
                    <span>送出訊息</span>
                    <i className="ri-arrow-right-line text-lg"></i>
                  </>
                )}
              </button>

              {submitStatus === 'success' && (
                <div className="p-4 border border-amber-500/40 bg-amber-500/10 rounded-sm text-amber-300 text-sm" style={{ fontFamily: "'Noto Sans TC', sans-serif" }}>
                  ✦ 訊息已成功送出！我們會盡快與您聯繫。
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="p-4 border border-red-500/40 bg-red-500/10 rounded-sm text-red-300 text-sm" style={{ fontFamily: "'Noto Sans TC', sans-serif" }}>
                  送出失敗，請稍後再試或直接聯絡我們。
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
